import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHead from "@/components/base/SectionHead";
import { gossipPosts } from "@/mocks/dailyContest";

const cats = ["全部", "旧料考古", "导师争议", "镜头考古", "数据风波", "关系反转", "规则科普"];

export default function Gossip() {
  const [active, setActive] = useState("全部");
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [likes, setLikes] = useState<Record<string, number>>({
    "gossip-1": 2841,
    "gossip-2": 1906,
    "gossip-3": 1277,
    "gossip-4": 913,
    "gossip-5": 806,
    "gossip-6": 668,
  });

  const list = useMemo(
    () => (active === "全部" ? gossipPosts : gossipPosts.filter((p) => p.tag === active)),
    [active]
  );

  const toggleLike = (id: string) => {
    setLiked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      setLikes((cur) => ({ ...cur, [id]: cur[id] + (next[id] ? 1 : -1) }));
      return next;
    });
  };

  return (
    <section id="gossip" className="border-b border-background-300/70 bg-background-50 py-12 md:py-16">
      <div className="container-x">
        <Reveal>
          <SectionHead
            eyebrow="热议八卦 · 证据吃瓜"
            title="瓜要吃得明明白白"
            desc="选手与导师的热议旧料统一按“呈现证据、不做定论”整理：标注时间与出处，存疑信息一律明说，绝不带节奏。"
            accent
          />
        </Reveal>

        <Reveal delay={60}>
          <div className="mt-6 inline-flex flex-wrap gap-2 rounded-full bg-background-100 p-1">
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                  active === c
                    ? "bg-accent-500 text-background-50"
                    : "text-foreground-600 hover:text-accent-600"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={i * 50}>
              <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300/70 bg-background-100 transition-colors hover:border-accent-300">
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.title} 相关配图`}
                    title={`每日大赛网 · ${p.title}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="chip absolute left-3 top-3 bg-background-950/75 text-background-100">
                    <i className="ri-mac-line mr-1" aria-hidden="true" />
                    {p.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <a href="#faq" className="cursor-pointer font-serif text-[16px] font-bold leading-6 text-foreground-950 transition-colors group-hover:text-accent-700">
                    {p.title}
                  </a>
                  <p className="mt-2 line-clamp-3 flex-1 text-[13px] leading-6 text-foreground-600">{p.lead}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-300/60 pt-3">
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-time-line" aria-hidden="true" />
                      {p.time}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-medium text-foreground-500">
                      <i className="ri-eye-line" aria-hidden="true" />
                      {p.hot}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleLike(p.id)}
                      aria-pressed={!!liked[p.id]}
                      className={`inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                        liked[p.id]
                          ? "border-accent-300 bg-accent-50 text-accent-600"
                          : "border-background-300 text-foreground-500 hover:border-accent-300 hover:text-accent-600"
                      }`}
                    >
                      <i className={liked[p.id] ? "ri-heart-fill" : "ri-heart-line"} aria-hidden="true" />
                      {likes[p.id] || 0}
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <p className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-shield-check-line text-secondary-600" aria-hidden="true" />
            标注原则：本板块仅呈现已公开信息与可考时间线，对未证实内容明确标注"存疑"，不进行主观定性。
          </p>
        </Reveal>
      </div>
    </section>
  );
}